// src/pages/Programs.js - UPDATED VERSION with Navigation to Program Details
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import { 
  FaMicrophone,
  FaUsers,
  FaSpellCheck,
  FaBookOpen,
  FaPalette,
  FaFootballBall,
  FaCrown,
  FaGraduationCap,
  FaHandshake,
  FaSpinner,
  FaExclamationTriangle,
  FaClock,
  FaStar,
  FaChevronRight,
  FaCheckCircle,
  FaCheck,
  FaEye
} from 'react-icons/fa';
import { programsAPI } from '../services/api';

const Programs = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [programs, setPrograms] = useState([]);
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalStudents: 0,
    totalPrograms: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollingPrograms, setEnrollingPrograms] = useState(new Set());
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();

  // Icon mapping for programs
  const iconMapping = {
    'FaCrown': FaCrown,
    'FaMicrophone': FaMicrophone,
    'FaUsers': FaUsers,
    'FaSpellCheck': FaSpellCheck,
    'FaBookOpen': FaBookOpen,
    'FaPalette': FaPalette,
    'FaFootballBall': FaFootballBall,
    'FaGraduationCap': FaGraduationCap,
    'debate': FaUsers,
    'speaking': FaMicrophone,
    'spelling': FaSpellCheck,
    'storytelling': FaBookOpen,
    'art': FaPalette,
    'sports': FaFootballBall,
    'eagles': FaCrown,
    'public_speaking': FaMicrophone,
    'debate_training': FaUsers,
    'spelling_bee': FaSpellCheck,
    'art_programs': FaPalette,
    'eagles_club': FaCrown,
    'default': FaGraduationCap
  };

  // Color schemes for different program types
  const colorSchemes = {
    'core': 'green',
    'academic': 'blue', 
    'cultural': 'yellow',
    'creative': 'pink',
    'premium': 'red',
    'debate': 'green',
    'speaking': 'blue',
    'spelling': 'purple',
    'storytelling': 'yellow',
    'art': 'pink',
    'sports': 'orange',
    'eagles': 'red',
    'default': 'gray'
  };

  const getColorClasses = (category, variant = 'primary') => {
    const scheme = colorSchemes[category] || 'gray';
    const schemes = {
      green: {
        primary: 'text-green-600',
        bg: 'bg-green-50',
        badge: 'bg-green-100 text-green-700 border-green-200',
        gradient: 'from-green-500 to-emerald-600',
        glow: '0 0 20px rgba(34, 197, 94, 0.5)',
        hover: 'hover:bg-green-50 hover:text-green-600'
      },
      blue: {
        primary: 'text-blue-600',
        bg: 'bg-blue-50',
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        gradient: 'from-blue-500 to-blue-600',
        glow: '0 0 20px rgba(59, 130, 246, 0.5)',
        hover: 'hover:bg-blue-50 hover:text-blue-600'
      },
      purple: {
        primary: 'text-purple-600',
        bg: 'bg-purple-50',
        badge: 'bg-purple-100 text-purple-700 border-purple-200',
        gradient: 'from-purple-500 to-purple-600',
        glow: '0 0 20px rgba(147, 51, 234, 0.5)',
        hover: 'hover:bg-purple-50 hover:text-purple-600'
      },
      yellow: {
        primary: 'text-yellow-600',
        bg: 'bg-yellow-50',
        badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        gradient: 'from-yellow-500 to-yellow-600',
        glow: '0 0 20px rgba(234, 179, 8, 0.5)',
        hover: 'hover:bg-yellow-50 hover:text-yellow-600'
      },
      pink: {
        primary: 'text-pink-600',
        bg: 'bg-pink-50',
        badge: 'bg-pink-100 text-pink-700 border-pink-200',
        gradient: 'from-pink-500 to-pink-600',
        glow: '0 0 20px rgba(219, 39, 119, 0.5)',
        hover: 'hover:bg-pink-50 hover:text-pink-600'
      },
      orange: {
        primary: 'text-orange-600',
        bg: 'bg-orange-50',
        badge: 'bg-orange-100 text-orange-700 border-orange-200',
        gradient: 'from-orange-500 to-orange-600',
        glow: '0 0 20px rgba(234, 88, 12, 0.5)',
        hover: 'hover:bg-orange-50 hover:text-orange-600'
      },
      red: {
        primary: 'text-red-600',
        bg: 'bg-red-50',
        badge: 'bg-red-100 text-red-700 border-red-200',
        gradient: 'from-red-500 to-red-600',
        glow: '0 0 20px rgba(220, 38, 38, 0.5)',
        hover: 'hover:bg-red-50 hover:text-red-600'
      },
      gray: {
        primary: 'text-gray-600',
        bg: 'bg-gray-50',
        badge: 'bg-gray-100 text-gray-700 border-gray-200',
        gradient: 'from-gray-500 to-gray-600',
        glow: '0 0 20px rgba(107, 114, 128, 0.5)',
        hover: 'hover:bg-gray-50 hover:text-gray-600'
      }
    };
    return schemes[scheme][variant] || schemes.gray[variant];
  };

  const getIconForProgram = (program) => {
    if (program.icon && iconMapping[program.icon]) {
      return iconMapping[program.icon];
    }
    
    const name = program.name?.toLowerCase() || program.title?.toLowerCase() || '';
    const type = program.type?.toLowerCase() || program.category?.toLowerCase() || '';
    
    for (const [key, Icon] of Object.entries(iconMapping)) {
      if (name.includes(key) || type.includes(key)) {
        return Icon;
      }
    }
    return iconMapping.default;
  };

  const getProgramCategory = (program) => {
    if (program.category) return program.category;
    
    const name = program.name?.toLowerCase() || program.title?.toLowerCase() || '';
    const type = program.type?.toLowerCase() || '';
    
    if (name.includes('debate') || type.includes('debate')) return 'core';
    if (name.includes('speaking') || type.includes('speaking')) return 'academic';
    if (name.includes('spelling') || type.includes('spelling')) return 'academic';
    if (name.includes('story') || type.includes('story')) return 'cultural';
    if (name.includes('art') || type.includes('art')) return 'creative';
    if (name.includes('sport') || type.includes('sport')) return 'creative';
    if (name.includes('eagles') || type.includes('eagles') || name.includes('elite')) return 'premium';
    
    return 'core';
  };

  const isUserEnrolledInProgram = (program) => {
    if (!isSignedIn || !program || !user) return false;

    const isInEnrolledState = enrolledPrograms.some(ep =>
      ep && (ep._id === program._id || ep.programId === program._id)
    );

    const isInProgramUsers = program.enrolledUsers && Array.isArray(program.enrolledUsers) &&
      program.enrolledUsers.some(enrolledUser => {
        if (!enrolledUser) return false;

        const userIdMatch = enrolledUser.userId === user.id;
        const emailMatch = enrolledUser.userEmail === user.primaryEmailAddress?.emailAddress;
        const activeStatus = enrolledUser.status === 'enrolled' || enrolledUser.status === 'waitlist';

        return (userIdMatch || emailMatch) && activeStatus;
      });

    return isInEnrolledState || isInProgramUsers;
  };

  useEffect(() => {
    if (!isLoaded) return;

    fetchPrograms();
    fetchStats();

    if (isSignedIn) {
      fetchUserEnrolledPrograms();
    }
  }, [isSignedIn, isLoaded]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await programsAPI.getAll();
      
      if (response.success) {
        setPrograms(response.programs || []);
      } else if (Array.isArray(response)) {
        setPrograms(response);
      } else {
        setPrograms([]);
      }
    } catch (err) {
      toast.error('Failed to load programs');
      setError(err.message || 'Failed to load programs');
      setPrograms(getDefaultPrograms());
    } finally {
      setLoading(false);
    }
  };

  const fetchUserEnrolledPrograms = async () => {
    try {
      const response = await programsAPI.getUserPrograms();
      
      if (response.success) {
        const userPrograms = response.programs || [];
        setEnrolledPrograms(userPrograms);
      }
    } catch (err) {
      setEnrolledPrograms([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await programsAPI.getStats();
      
      if (response.success && response.stats) {
        setStats({
          totalSchools: response.stats.totalSchools || 100,
          totalStudents: response.stats.totalStudents || 5000,
          totalPrograms: response.stats.totalPrograms || 6
        });
      }
    } catch (err) {
      setStats({
        totalSchools: 100,
        totalStudents: 5000,
        totalPrograms: 6
      });
    }
  };

  // Navigate to program details page
  const handleProgramClick = (programId, e) => {
    // Prevent navigation if clicking on the enroll button
    if (e.target.closest('.enroll-button')) {
      return;
    }
    navigate(`/programs/${programId}`);
  };

  const handleEnrollment = async (programId, programTitle, e) => {
    e.stopPropagation(); // Prevent program card click navigation

    if (!isSignedIn) {
      // Do not use localStorage for auth flows. Pass intended program via router state
      toast.error('Login to register');
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            from: '/programs', 
            message: 'Login to register',
            programId: programId 
          } 
        });
      }, 1000);
      return;
    }

    try {
      setEnrollingPrograms(prev => new Set([...prev, programId]));
      
      const response = await programsAPI.enroll(programId);
      
      if (response.success) {
        const updatedProgram = response.program;
        setEnrolledPrograms(prev => {
          const isAlreadyInState = prev.some(p => p._id === programId);
          if (isAlreadyInState) return prev;
          return [...prev, updatedProgram];
        });

        setPrograms(prev => prev.map(p => 
          p._id === programId ? updatedProgram : p
        ));

        toast.success('Successfully enrolled!');

        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(response.message || 'Enrollment failed');
      }
    } catch (err) {
      toast.error(err.message || 'Enrollment failed');
    } finally {
      setEnrollingPrograms(prev => {
        const newSet = new Set(prev);
        newSet.delete(programId);
        return newSet;
      });
    }
  };

  const getThumbnailUrl = (program) => {
    if (program.thumbnail) {
      if (program.thumbnail.startsWith('http')) {
        return program.thumbnail;
      }
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      return `${baseUrl}${program.thumbnail.startsWith('/') ? '' : '/'}${program.thumbnail}`;
    }
    
    const category = getProgramCategory(program);
    const fallbackImages = {
      'core': 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=250&fit=crop',
      'academic': 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=250&fit=crop',
      'cultural': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop',
      'creative': 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=250&fit=crop',
      'premium': 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=400&h=250&fit=crop'
    };
    
    return fallbackImages[category] || fallbackImages['core'];
  };

  const getDefaultPrograms = () => [
    {
      _id: '1',
      title: 'Debate Training',
      name: 'Debate Training',
      description: 'Training students to think critically, articulate their thoughts, and develop persuasive arguments through structured debate programs.',
      features: ['Critical thinking development', 'Argument construction', 'Research skills', 'Inter-school competitions'],
      duration: 'Year-round',
      badge: 'Core Program',
      category: 'core',
      price: { amount: 0, currency: 'KES' },
      isActive: true,
      icon: 'FaUsers'
    },
    {
      _id: '2',
      title: 'Public Speaking',
      name: 'Public Speaking',
      description: 'Developing confident, impactful communicators through comprehensive public speaking training and performance coaching.',
      features: ['Speech techniques', 'Confidence building', 'Stage presence', 'Presentation skills'],
      duration: '3-month intensive',
      badge: 'Popular',
      category: 'academic',
      price: { amount: 0, currency: 'KES' },
      isActive: true,
      icon: 'FaMicrophone'
    },
    {
      _id: '3',
      title: 'Spelling Bee',
      name: 'Spelling Bee',
      description: 'Enhancing vocabulary, focus, and mental agility through competitive spelling bee programs and word mastery techniques.',
      features: ['Vocabulary expansion', 'Memory techniques', 'Competition prep', 'Mental agility training'],
      duration: '6-month program',
      badge: 'Academic',
      category: 'academic',
      price: { amount: 0, currency: 'KES' },
      isActive: true,
      icon: 'FaSpellCheck'
    },
    {
      _id: '4',
      title: 'Storytelling',
      name: 'Storytelling',
      description: 'Nurturing creativity and cultural pride through narratives, celebrating African heritage and developing narrative skills.',
      features: ['Creative writing', 'Cultural storytelling', 'Narrative techniques', 'Performance storytelling'],
      duration: '4-month program',
      badge: 'Cultural',
      category: 'cultural',
      price: { amount: 0, currency: 'KES' },
      isActive: true,
      icon: 'FaBookOpen'
    },
    {
      _id: '5',
      title: 'Art Programs',
      name: 'Art Programs',
      description: 'Encouraging self-expression through visual and performing arts, fostering creativity and artistic development.',
      features: ['Visual arts', 'Performing arts', 'Creative expression', 'Art exhibitions'],
      duration: 'Ongoing workshops',
      badge: 'Creative',
      category: 'creative',
      price: { amount: 0, currency: 'KES' },
      isActive: true,
      icon: 'FaPalette'
    },
    {
      _id: '6',
      title: 'EAGLES CLUB',
      name: 'EAGLES CLUB',
      description: 'Elite private training for debate and public speaking mastery, grooming and etiquette, handling stage fright and building stage presence.',
      features: ['Elite coaching', 'Grooming & etiquette', 'Stage fright management', 'Leadership development'],
      duration: '12-month exclusive',
      badge: 'Premium',
      category: 'premium',
      price: { amount: 500, currency: 'KES' },
      isActive: true,
      icon: 'FaCrown'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 sm:pt-24 flex items-center justify-center px-4">
        <div className="text-center">
          <FaSpinner className="w-8 h-8 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading programs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 sm:pt-20">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-3 py-1.5 bg-green-100 rounded-full border border-green-200 mb-4">
              <span className="text-green-700 text-xs font-medium">Comprehensive Training Programs</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-4">
              <span className="text-gray-900">Programs That</span>
              <br />
              <span className="text-green-600 drop-shadow-sm">Develop,</span>{' '}
              <span className="text-yellow-600 drop-shadow-sm">Inspire &</span>{' '}
              <span className="text-red-600 drop-shadow-sm">Transform.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
              Comprehensive programs designed to develop confident leaders through debate, public speaking, arts, technology and cultural activities.
            </p>

            {error && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-center text-yellow-700 mx-4">
                <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Programs Grid */}
      <section ref={ref} className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {programs.map((program, index) => {
              const Icon = getIconForProgram(program);
              const category = getProgramCategory(program);
              const isEaglesClub = category === 'premium';
              const isEnrolling = enrollingPrograms.has(program._id);
              const isEnrolled = isUserEnrolledInProgram(program);
              const displayTitle = program.title || program.name;
              const displayPrice = program.price?.amount || 0;
              
              return (
                <motion.div
                  key={program._id || index}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={(e) => handleProgramClick(program._id, e)}
                >
                  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden h-full hover:scale-[1.02] hover:shadow-xl hover:border-gray-300 transition-all duration-300 shadow-md">
                    
                    {/* Program Image */}
                    <div className="h-36 sm:h-48 relative overflow-hidden">
                      <img
                        src={getThumbnailUrl(program)}
                        alt={displayTitle}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=400&h=250&fit=crop';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-black/40"></div>

                      {/* Enrolled Badge */}
                      {isEnrolled && (
                        <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center shadow-lg border border-white/20">
                          <FaCheck className="mr-1 w-3 h-3" />
                          ENROLLED
                        </div>
                      )}

                      {/* Featured Badge */}
                      {program.isFeatured && !isEnrolled && (
                        <span className="absolute top-4 right-4 bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold shadow-lg border border-white/20">
                          FEATURED
                        </span>
                      )}

                      {/* Category Badge */}
                      <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold shadow-lg border border-white/20 ${getColorClasses(category, 'badge')}`}>
                        {program.badge || category.toUpperCase()}
                      </span>

                      {/* Icon */}
                      <div className="absolute bottom-4 left-4 p-2 rounded-xl bg-black/50 backdrop-blur-sm shadow-lg border border-white/20">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-6">
                      {/* Title */}
                      <h3 className="text-xl font-bold mb-3 group-hover:scale-105 transition-all duration-300">
                        <span className={`${getColorClasses(category, 'primary')} drop-shadow-lg`}>
                          {displayTitle}
                        </span>
                      </h3>
                      
                      {/* Description */}
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed group-hover:text-gray-700 transition-colors duration-300 line-clamp-3">
                        {program.description}
                      </p>

                      {/* Features */}
                      <div className="space-y-3 mb-4">
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2">Key Features:</h4>
                          <ul className="space-y-1">
                            {(program.features || []).slice(0, 3).map((feature, idx) => {
                              // Handle both string features and object features
                              const featureText = typeof feature === 'string' ? feature : feature.title || feature.description || '';
                              return (
                                <li key={idx} className="text-gray-600 text-xs flex items-center group-hover:text-gray-700 transition-colors duration-300">
                                  <div className={`w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0 ${getColorClasses(category, 'primary').replace('text-', 'bg-')} group-hover:bg-green-600 transition-colors duration-300`} />
                                  {featureText}
                                </li>
                              );
                            })}
                          </ul>
                        </div>

                        {/* Duration & Price */}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100 group-hover:text-gray-600 group-hover:border-gray-200 transition-all duration-300">
                          <div className="flex items-center">
                            <span className="font-medium">{program.duration || 'Flexible'}</span>
                          </div>
                          {isEaglesClub && (
                            <div className="flex items-center text-red-600 font-semibold">
                              <span>Exclusive</span>
                            </div>
                          )}
                          {displayPrice > 0 && (
                            <span className="font-semibold text-green-600">
                              {program.price?.currency || 'KES'} {displayPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* CTA Button */}
                      {isEnrolled ? (
                        <button 
                          onClick={() => navigate('/dashboard')}
                          className="enroll-button w-full px-4 py-2.5 bg-green-100 border-2 border-green-200 text-green-700 rounded-xl font-semibold text-sm hover:bg-green-200 transition-all duration-300 flex items-center justify-center group"
                        >
                          <FaCheckCircle className="mr-2" />
                          Enrolled - Go to Dashboard
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleEnrollment(program._id, displayTitle, e)}
                          disabled={isEnrolling}
                          className={`enroll-button w-full px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center transition-all duration-300 ${
                            isEnrolling
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : `${getColorClasses(category, 'primary')} border-2 border-transparent hover:border-gray-200 hover:bg-gray-50`
                          }`}
                        >
                          {isEnrolling ? (
                            <>
                              <FaSpinner className="animate-spin mr-2" />
                              Enrolling...
                            </>
                          ) : (
                            <>
                              <FaChevronRight className="mr-2" />
                              Enroll Now
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;